/**
 * AnimatedLogo Component
 *
 * Animated ECG-style logo for the application.
 * Animation goes from left to right, continuous loop.
 */


interface AnimatedLogoProps {
  size?: number;
  variant?: 'draw' | 'draw-dot' | 'pulse-draw' | 'continuous' | 'heartbeat';
  color?: string;
  glowColor?: string;
  duration?: number;
  className?: string;
}

export function AnimatedLogo({
  size = 24,
  variant = 'draw-dot',
  color = 'currentColor',
  glowColor = '#8b5cf6',
  duration = 2,
  className = '',
}: AnimatedLogoProps) {

  // Path length for the ECG line (approximate)
  const pathLength = 55;

  // Points reversed: left to right (2 -> 22)
  const ecgPoints = "2 12 6 12 9 3 15 21 18 12 22 12";
  const ecgPath = "M 2 12 L 6 12 L 9 3 L 15 21 L 18 12 L 22 12";

  const styles = `
    /* Base drawing animation - left to right */
    @keyframes ecg-draw-line {
      0% { stroke-dashoffset: ${pathLength}; }
      100% { stroke-dashoffset: 0; }
    }

    /* Continuous loop drawing - instant reset, no backward animation */
    @keyframes ecg-draw-loop {
      0% { stroke-dashoffset: ${pathLength}; }
      70% { stroke-dashoffset: 0; }
      85% { stroke-dashoffset: 0; }
      85.01% { stroke-dashoffset: ${pathLength}; }
      100% { stroke-dashoffset: ${pathLength}; }
    }

    /* Dot movement along path - left to right, instant reset */
    @keyframes ecg-move-dot {
      0% { offset-distance: 0%; opacity: 1; }
      70% { offset-distance: 100%; opacity: 1; }
      85% { offset-distance: 100%; opacity: 0; }
      85.01% { offset-distance: 0%; opacity: 0; }
      86% { offset-distance: 0%; opacity: 1; }
      100% { offset-distance: 0%; opacity: 1; }
    }

    /* Pulse glow effect */
    @keyframes ecg-pulse-glow {
      0%, 100% {
        filter: drop-shadow(0 0 2px ${glowColor}40);
      }
      50% {
        filter: drop-shadow(0 0 8px ${glowColor}) drop-shadow(0 0 16px ${glowColor}80);
      }
    }

    /* Heartbeat scale */
    @keyframes ecg-heartbeat-scale {
      0%, 100% { transform: scale(1); }
      15% { transform: scale(1.15); }
      30% { transform: scale(1); }
      45% { transform: scale(1.1); }
      60% { transform: scale(1); }
    }

    .ecg-line-draw {
      stroke-dasharray: ${pathLength};
      stroke-dashoffset: ${pathLength};
      animation: ecg-draw-line ${duration}s ease-out forwards;
    }

    .ecg-line-loop {
      stroke-dasharray: ${pathLength};
      stroke-dashoffset: ${pathLength};
      animation: ecg-draw-loop ${duration}s ease-in-out infinite;
    }

    .ecg-line-continuous {
      stroke-dasharray: ${pathLength};
      animation: ecg-draw-loop ${duration}s ease-in-out infinite;
    }

    .ecg-line-pulse {
      stroke-dasharray: ${pathLength};
      stroke-dashoffset: ${pathLength};
      animation: ecg-draw-line ${duration}s ease-out forwards, ecg-pulse-glow ${duration / 2}s ease-in-out infinite;
    }

    .ecg-dot-loop {
      offset-path: path('${ecgPath}');
      animation: ecg-move-dot ${duration}s ease-in-out infinite;
    }

    .ecg-heartbeat {
      animation: ecg-heartbeat-scale 1.5s ease-in-out infinite;
    }

    .ecg-glow {
      animation: ecg-pulse-glow 2s ease-in-out infinite;
    }
  `;

  // Variant: Simple draw animation (one-time)
  if (variant === 'draw') {
    return (
      <div className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{styles}</style>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline
            points={ecgPoints}
            className="ecg-line-draw"
          />
        </svg>
      </div>
    );
  }

  // Variant: Draw with following dot (continuous loop)
  if (variant === 'draw-dot') {
    return (
      <div className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <style>{styles}</style>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          style={{ overflow: 'visible' }}
        >
          {/* Glow effect behind */}
          <polyline
            points={ecgPoints}
            stroke={glowColor}
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.3"
            className="ecg-line-loop"
            style={{ filter: `blur(3px)` }}
          />
          {/* Main line */}
          <polyline
            points={ecgPoints}
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="ecg-line-loop"
          />
          {/* Following dot */}
          <circle
            r="3"
            fill={glowColor}
            className="ecg-dot-loop"
            style={{ filter: `drop-shadow(0 0 4px ${glowColor})` }}
          />
        </svg>
      </div>
    );
  }

  // Variant: Pulse draw with glow
  if (variant === 'pulse-draw') {
    return (
      <div className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{styles}</style>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="ecg-glow"
        >
          <polyline
            points={ecgPoints}
            className="ecg-line-pulse"
          />
        </svg>
      </div>
    );
  }

  // Variant: Continuous loop
  if (variant === 'continuous') {
    return (
      <div className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{styles}</style>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline
            points={ecgPoints}
            className="ecg-line-continuous"
          />
        </svg>
      </div>
    );
  }

  // Variant: Heartbeat with scale
  if (variant === 'heartbeat') {
    return (
      <div className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{styles}</style>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="ecg-heartbeat ecg-glow"
        >
          <polyline points={ecgPoints} />
        </svg>
      </div>
    );
  }

  // Default fallback
  return (
    <div className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points={ecgPoints} />
      </svg>
    </div>
  );
}

/**
 * Demo component to preview all variants
 */
export function AnimatedLogoDemo() {
  const variants = ['draw', 'draw-dot', 'pulse-draw', 'continuous', 'heartbeat'] as const;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      padding: '24px',
      background: 'rgba(0,0,0,0.5)',
      borderRadius: '16px'
    }}>
      <h3 style={{ color: 'white', margin: 0 }}>Animation Variants</h3>
      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
        {variants.map(variant => (
          <div key={variant} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            padding: '16px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '12px',
            minWidth: '100px'
          }}>
            <AnimatedLogo
              size={32}
              variant={variant}
              color="white"
              glowColor="#8b5cf6"
              duration={2}
            />
            <span style={{ color: 'var(--muted)', fontSize: '12px' }}>{variant}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
